import { Icon } from "../../../ui/Icon";
import { useMemo } from "react";
import { Toolbar } from "../../../ui/Toolbar";
import { Surface } from "../../../ui/Surface";
import { DropdownButton, DropdownCategoryTitle } from "../../../ui/Dropdown";
import { Popover } from "../../../ui/Popover";
import Box from "@mui/material/Box";
import { ContentPickerOptions, ContentTypePickerOption, ContentTypePickerCategory } from "../types";

export type ContentTypePickerProps = {
    options: ContentPickerOptions;
    /** id of `ContentPickerOptions` in `useTextmenuContentTypes` */
    disabled?: (
        | "hierarchy"
        | "paragraph"
        | "heading1"
        | "heading2"
        | "heading3"
        | "lists"
        | "bulletList"
        | "numberedList"
        | "todoList"
    )[];
};

const isOption = (option: ContentTypePickerOption | ContentTypePickerCategory): option is ContentTypePickerOption =>
    option.type === "option";

const isCategory = (option: ContentTypePickerOption | ContentTypePickerCategory): option is ContentTypePickerCategory =>
    option.type === "category";

export const ContentTypePicker = ({ options, disabled }: ContentTypePickerProps) => {
    const activeItem = useMemo(() => options.find(option => option.type === "option" && option.isActive()), [options]);

    return (
        <Popover
            content={({ closePopup }) => (
                <Surface style={{ display: "flex", flexDirection: "column", gap: "4px", padding: "16px 8px" }}>
                    {options.map(option => {
                        if (disabled?.includes(option.id as any)) {
                            return null;
                        }

                        if (isOption(option)) {
                            return (
                                <DropdownButton
                                    key={option.id}
                                    onClick={() => {
                                        option.onClick();
                                        closePopup();
                                    }}
                                    isActive={option.isActive()}>
                                    <Icon
                                        name={option.icon}
                                        style={{ width: "16px", height: "16px", marginRight: "4px" }}
                                    />
                                    {option.label}
                                </DropdownButton>
                            );
                        } else if (isCategory(option)) {
                            return (
                                <Box sx={{ marginTop: "8px", "&:first-of-type": { marginTop: 0 } }} key={option.id}>
                                    <DropdownCategoryTitle key={option.id}>{option.label}</DropdownCategoryTitle>
                                </Box>
                            );
                        }
                    })}
                </Surface>
            )}>
            {({ openPopup }) => (
                <Toolbar.Button
                    tabIndex={-1}
                    active={activeItem?.id !== "paragraph" && !!activeItem?.type}
                    onClick={openPopup}>
                    <Icon name={(activeItem?.type === "option" && activeItem.icon) || "Pilcrow"} />
                    <Icon name="ChevronDown" style={{ width: "8px", height: "8px" }} />
                </Toolbar.Button>
            )}
        </Popover>
    );
};
